// src/components/comunicacion/BuzonSugerencias.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ENDPOINTS } from "../../Utils/Endpoints";
import { getSessionUserId, getSessionEmpresaId } from "../../../../utils/SessionUser";
import { ENDPOINTS as ENDPOINTS_SEG } from "../../../Seguridad/Utils/Endpoints";

const ESTADOS = [
  { value: "todas", label: "Todas" },
  { value: "recibida", label: "Recibidas" },
  { value: "en_revision", label: "En revisión" },
  { value: "atendida", label: "Atendidas" },
];

const BuzonSugerencias = ({ onNavigate }) => {
  const [dbCategorias, setDbCategorias] = useState([]);
  const [dbEstados, setDbEstados] = useState([]);
  const [dbUsuarios, setDbUsuarios] = useState([]);
  const [sugerencias, setSugerencias] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [nuevaSug, setNuevaSug] = useState({ asunto: "", descripcion: "", idCategoria: "", anonimo: false });
  const [erroresNueva, setErroresNueva] = useState({});
  const [filtrosAdmin, setFiltrosAdmin] = useState({ texto: "", categoria: "Todas", estado: "todas" });
  const [showModal, setShowModal] = useState(false);

  const ESTADO_TO_DB = { "recibida": "recibida", "en_revision": "en_revision", "atendida": "atendida" };
  const DB_TO_ESTADO = { "recibida": "recibida", "en_revision": "en_revision", "atendida": "atendida" };

  const [myRoles, setMyRoles] = useState([]);
  const ADMIN_ROLES = [1, 1003, 1009, 1008];

  const amIAdmin = useMemo(() => {
    if (!myRoles || myRoles.length === 0) return false;
    return myRoles.some(r => {
      const rid = r.idRol ?? r.IdRol ?? r.id ?? r.Id;
      return ADMIN_ROLES.some(adminRole => Number(adminRole) === Number(rid));
    });
  }, [myRoles]);

  const cargarDatos = async () => {
    const currentUserId = getSessionUserId();
    try {
      let cats = [];
      try { 
        const res = await axios.get(ENDPOINTS.categorias); 
        const catsNorm = (res.data || []).map(c => ({
          idCategoria: c.IdCategoria ?? c.idCategoria,
          nombre: c.Nombre ?? c.nombre ?? ""
        }));

        const categoriasRelevantes = [
          "recursos humanos", "rh", "capacitacion", "capacitación", "clima laboral", 
          "comunicación", "comunicacion", "servicios internos", "innovación", "innovacion", 
          "calidad", "mejora continua", "procesos", "tecnología", "tecnologia", 
          "cultura organizacional", "seguridad", "social"
        ];

        const filtered = catsNorm.filter(c => 
          categoriasRelevantes.some(r => c.nombre.toLowerCase().includes(r))
        );

        const finalCats = filtered.length > 0 ? filtered : catsNorm;
        setDbCategorias(finalCats); 
        cats = finalCats;

        if (finalCats.length > 0) {
          setNuevaSug(prev => ({ ...prev, idCategoria: finalCats[0].idCategoria }));
        }
      } catch (e) { console.error("Error cats", e); }

      let myUserRoles = [];
      try {
        const resRol = await axios.get(ENDPOINTS_SEG.usuarioRolByUsuarioId(currentUserId));
        myUserRoles = resRol.data;
        setMyRoles(myUserRoles);
      } catch (e) { console.error("Error roles usuario", e); }

      let allUsersEligible = [];
      try {
        const allowedRoles = [...ADMIN_ROLES, 1011];
        const userPromises = allowedRoles.map(rid =>
          axios.get(ENDPOINTS_SEG.usuariosByRol(rid))
            .then(res => ({ rid, data: res.data || [] }))
            .catch(() => ({ rid, data: [] }))
        );
        const userResults = await Promise.all(userPromises);
        const mapRes = new Map();
        userResults.flatMap(r => r.data).forEach(u => {
          const uid = u.idUsuario || u.IdUsuario;
          if (uid && !mapRes.has(uid)) {
            mapRes.set(uid, {
              idUsuario: uid,
              nombreUsuario: u.nombreUsuario || u.NombreUsuario,
              nombrePersona: u.nombrePersona || u.NombrePersona
            });
          }
        });
        allUsersEligible = Array.from(mapRes.values());
        setDbUsuarios(allUsersEligible);
      } catch (e) { console.error("Error cargando usuarios por rol", e); }

      const resAllUsers = await axios.get(ENDPOINTS.usuarios);
      const allUsers = resAllUsers.data;

      const resSug = await axios.get(ENDPOINTS.sugerenciasDashboardByUsuarioId(currentUserId));
      const raw = resSug.data;

      // Unir estados locales con los de BD para obtener IDs precisos
      let ests = [];
      try { const resE = await axios.get(ENDPOINTS.estadosIntranet); ests = resE.data; setDbEstados(ests); } catch (e) { console.error("Error estados", e); }

      const hydrated = raw.map(s => {
        const idCat = s.idCategoria || s.IdCategoria;
        const catObj = cats.find(c => (c.idCategoria || c.IdCategoria) === idCat);
        const catName = catObj ? (catObj.nombre || catObj.Nombre) : "General";

        const idEstActual = s.idEstado || s.IdEstado;
        const estObj = ests.find(e => (e.idEstado || e.IdEstado) === idEstActual);
        const estName = estObj ? (estObj.nombre || estObj.Nombre || estObj.clave || estObj.Clave || 'recibida') : 'recibida';
        const estNameNorm = normalizeStr(estName);

        let estKey = "recibida";
        if (estNameNorm.includes("revision")) estKey = "en_revision";
        else if (estNameNorm.includes("atendida") || estNameNorm.includes("completada")) estKey = "atendida";
        else if (estNameNorm.includes("recibida")) estKey = "recibida";
        else {
          // Fallback a mapeo directo si no hay palabras clave
          estKey = DB_TO_ESTADO[estName.toLowerCase().trim()] || "recibida";
        }

        const idAutor = s.idUsuarioAutor || s.IdUsuarioAutor;
        const usuarioAutor = allUsers.find(u => (u.idUsuario || u.IdUsuario) == idAutor);
        const autorNombre = usuarioAutor
          ? (usuarioAutor.nombrePersona || usuarioAutor.NombrePersona || usuarioAutor.nombreUsuario || usuarioAutor.NombreUsuario)
          : (idAutor ? `Usuario ${idAutor}` : "Anónimo");

        return {
          id: s.idSugerencia || s.IdSugerencia,
          asunto: s.asunto || s.Asunto,
          descripcion: s.descripcion || s.Descripcion,
          categoria: catName,
          estado: estKey, // interno: recibida, en_revision, atendida
          idEstado: idEstActual,
          autor: autorNombre,
          idAutor: idAutor,
          idResponsable: s.idResponsable || s.IdResponsable,
          responsable: s.responsable || s.Responsable || "Pendiente",
          comentariosInternos: s.comentariosInternos || s.ComentariosInternos || "",
          fechaEnvio: s.fechaEnvio || s.FechaEnvio ? new Date(s.fechaEnvio || s.FechaEnvio).toLocaleDateString() : "-",
          anonimo: s.esAnonima || s.EsAnonima,
          puedeActualizar: s.puedeActualizar || s.PuedeActualizar,
          _original: s
        };
      });
      setSugerencias(hydrated);
    } catch (e) { console.error("Error cargar sugerencias", e); }
  };

  useEffect(() => { cargarDatos(); }, []);

  // Helper para normalizar búsquedas (sin acentos, minúsculas)
  const normalizeStr = (str) => {
    if (!str) return "";
    return str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
  };

  const updateSugerenciaBackend = async (id, campos = {}) => {
    const sug = sugerencias.find(s => s.id === id);
    if (!sug) return false;

    // 1. Determinar ID de Estado (vital para evitar FK 547)
    const newEstadoKey = campos.estado || sug.estado;
    let finalIdEstado = Number(sug._original.idEstado || sug._original.IdEstado || 1);

    if (newEstadoKey) {
      const dbTargetName = ESTADO_TO_DB[newEstadoKey];
      const targetNorm = normalizeStr(dbTargetName);

      const foundEst = dbEstados.find(e => {
        const n = normalizeStr(e.nombre || e.Nombre);
        const c = normalizeStr(e.clave || e.Clave);
        return targetNorm && (n === targetNorm || c === targetNorm);
      });

      if (foundEst) {
        finalIdEstado = Number(foundEst.idEstado || foundEst.IdEstado);
        console.log(`[BuzonSugerencias] Mapeando estado '${newEstadoKey}' -> '${ESTADO_TO_DB[newEstadoKey]}' -> ID: ${finalIdEstado}`);
      } else {
        console.warn(`[BuzonSugerencias] No se encontró coincidencia para '${newEstadoKey}' (${ESTADO_TO_DB[newEstadoKey]}) en dbEstados:`, dbEstados);
        console.warn(`[BuzonSugerencias] Manteniendo ID de estado original: ${finalIdEstado}`);
      }
    }

    // 2. Determinar ID de Responsable (asegurar Number o null)
    let finalIdResponsable = null;
    if (campos.idResponsable !== undefined) {
      finalIdResponsable = campos.idResponsable ? Number(campos.idResponsable) : null;
    } else {
      const oldId = sug.idResponsable || sug._original.idResponsable || sug._original.IdResponsable;
      finalIdResponsable = oldId ? Number(oldId) : null;
    }

    // 3. Otros campos
    const finalComentarios = campos.comentariosInternos !== undefined ? campos.comentariosInternos : sug.comentariosInternos;
    const finalNombreResp = campos.responsable !== undefined ? campos.responsable : sug.responsable;

    // 4. Construir Payload Robusto (PascalCase para el C# DTO)
    const payload = {
      IdSugerencia: id,
      Asunto: sug.asunto || sug._original.Asunto || sug._original.asunto,
      Descripcion: sug.descripcion || sug._original.Descripcion || sug._original.descripcion,
      IdCategoria: Number(sug._original.idCategoria || sug._original.IdCategoria),
      EsAnonima: !!(sug.anonimo),
      IdUsuarioAutor: sug._original.IdUsuarioAutor !== undefined ? sug._original.IdUsuarioAutor : (sug._original.idUsuarioAutor !== undefined ? sug._original.idUsuarioAutor : null),
      IdUsuarioCreador: sug._original.IdUsuarioCreador !== undefined ? sug._original.IdUsuarioCreador : (sug._original.idUsuarioCreador !== undefined ? sug._original.idUsuarioCreador : null),
      IdResponsable: finalIdResponsable,
      IdEstado: finalIdEstado,
      Responsable: finalNombreResp || "Pendiente",
      ComentariosInternos: finalComentarios || "",
      FechaEnvio: sug._original.FechaEnvio || sug._original.fechaEnvio || new Date().toISOString(),
      FechaModificacion: new Date().toISOString(),
      IdUsuarioModificador: Number(getSessionUserId()),
      Activo: true
    };

    try {
      console.log("[BuzonSugerencias] Enviando PUT a backend:", id, payload);
      const res = await axios.put(ENDPOINTS.sugerencias, payload);
      console.log("[BuzonSugerencias] Respuesta de actualización:", res.data);

      // Forzar recarga completa para asegurar sincronía con la BD
      await cargarDatos();
      return true;
    } catch (e) {
      const errorMsg = e.response?.data ? JSON.stringify(e.response.data) : e.message;
      console.error("Error crítico al actualizar sugerencia:", errorMsg, payload);
      return false;
    }
  };

  const getEstadoLabel = (estado) => {
    if (estado === "recibida") return "Recibida";
    if (estado === "en_revision") return "En revisión";
    if (estado === "atendida") return "Atendida";
    return "Recibida";
  };

  const sugerenciasBaseAdmin = useMemo(() => {
    // Solo aplicar filtro de responsable si los roles ya cargaron Y no es admin
    if (myRoles.length > 0 && !amIAdmin) {
      const myId = getSessionUserId();
      return sugerencias.filter(s => s.idResponsable == myId);
    }
    return sugerencias;
  }, [sugerencias, amIAdmin, myRoles]);

  const sugerenciasFiltradasAdmin = useMemo(() => {
    return sugerenciasBaseAdmin.filter(s => {
      if (filtrosAdmin.texto && !(s.asunto.toLowerCase().includes(filtrosAdmin.texto.toLowerCase()) || s.descripcion.toLowerCase().includes(filtrosAdmin.texto.toLowerCase()))) return false;
      if (filtrosAdmin.categoria !== "Todas" && s.categoria !== filtrosAdmin.categoria) return false;
      if (filtrosAdmin.estado !== "todas" && s.estado !== filtrosAdmin.estado) return false;
      return true;
    });
  }, [sugerenciasBaseAdmin, filtrosAdmin]);

  const sugerenciaSeleccionada = useMemo(() => sugerencias.find(s => s.id === selectedId) || null, [sugerencias, selectedId]);

  // Stats sobre la base sin filtros de búsqueda (consistente con donut)
  const stats = useMemo(() => {
    const base = sugerenciasBaseAdmin;
    const total = base.length;
    const recibidas = base.filter(s => s.estado === "recibida").length;
    const enRevision = base.filter(s => s.estado === "en_revision").length;
    const atendidas = base.filter(s => s.estado === "atendida").length;
    return { total, recibidas, enRevision, atendidas };
  }, [sugerenciasBaseAdmin]);

  const handleNuevaSugChange = e => {
    const { name, value, type, checked } = e.target;
    setNuevaSug(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const validarNuevaSug = () => {
    const errs = {};
    if (!nuevaSug.asunto.trim()) errs.asunto = "El asunto es obligatorio.";
    if (!nuevaSug.descripcion.trim()) errs.descripcion = "La descripción es obligatoria.";
    setErroresNueva(errs);
    return Object.keys(errs).length === 0;
  };

  const handleEnviarSugerencia = async (e) => {
    e.preventDefault();
    if (!validarNuevaSug()) return;
    try {
      const catObj = dbCategorias.find(c => (c.idCategoria || c.IdCategoria) == nuevaSug.idCategoria);
      const idCat = catObj ? (catObj.idCategoria || catObj.IdCategoria) : null;
      // Buscar estado "Recibida" tolerante a PascalCase y camelCase (la API devuelve PascalCase)
      const estObj = dbEstados.find(e =>
        (e.nombre || e.Nombre || '').toLowerCase() === 'recibida' ||
        (e.clave || e.Clave || '').toLowerCase() === 'recibida'
      );
      const idEst = estObj ? (estObj.idEstado || estObj.IdEstado) : null;
      const payload = {
        IdSugerencia: 0,
        Asunto: nuevaSug.asunto,
        Descripcion: nuevaSug.descripcion,
        IdCategoria: idCat,
        EsAnonima: nuevaSug.anonimo,
        IdUsuarioAutor: nuevaSug.anonimo ? null : getSessionUserId(),
        IdUsuarioCreador: getSessionUserId(),
        IdResponsable: null,
        IdEstado: idEst,
        Responsable: "Pendiente de asignar",
        ComentariosInternos: "Sin comentarios",
        FechaEnvio: new Date().toISOString(),
        FechaModificacion: new Date().toISOString(),
        IdUsuarioModificador: getSessionUserId(),
        Activo: true,
        IdEmpresa: getSessionEmpresaId()
      };
      console.log("[BuzonSugerencias] Enviando POST (PascalCase):", payload);
      await axios.post(ENDPOINTS.sugerencias, payload);
      setNuevaSug({ asunto: "", descripcion: "", idCategoria: dbCategorias.length > 0 ? dbCategorias[0].idCategoria : "", anonimo: false });
      setErroresNueva({});
      setShowModal(false);
      cargarDatos();
    } catch (e) {
      const msg = e.response?.data ? JSON.stringify(e.response.data) : e.message;
      console.error("Error al enviar sugerencia:", msg, e);
    }
  };

  const handleFiltrosAdminChange = e => {
    const { name, value } = e.target;
    setFiltrosAdmin(prev => ({ ...prev, [name]: value }));
  };

  const handleSeleccionarSugerencia = sug => setSelectedId(sug.id);

  const handleCambiarEstado = (id, nuevoEstado) => {
    setSugerencias(prev => prev.map(s => s.id === id ? { ...s, estado: nuevoEstado } : s));
  };

  const handleCambiarResponsable = (idSugerencia, idUsuarioResp) => {
    // La lista dbUsuarios ya está hidratada, pero buscamos con flexibilidad por si acaso
    const userObj = dbUsuarios.find(u => (u.idUsuario || u.IdUsuario) == idUsuarioResp);
    const nombreResp = userObj
      ? (userObj.nombrePersona || userObj.NombrePersona || userObj.nombreUsuario || userObj.NombreUsuario || "Usuario")
      : "Sugerencias";
    setSugerencias(prev => prev.map(s => s.id === idSugerencia ? { ...s, responsable: nombreResp, idResponsable: idUsuarioResp } : s));
  };

  const handleCambiarComentariosInternos = (id, comentariosInternos) => {
    setSugerencias(prev => prev.map(s => s.id === id ? { ...s, comentariosInternos } : s));
  };

  const handleBlurComentarios = (id, val) => { /* espera botón Actualizar */ };

  return (
    <div className="intra-suggestions-page">
      {/* HEADER */}
      <header className="intra-news-header">
        <div>
          {onNavigate && (
            <button type="button" className="intra-button-ghost btn-cancel" onClick={() => onNavigate("dashboard")} style={{ marginBottom: '1rem' }}>
              <span style={{ fontSize: '1.2rem', lineHeight: '1' }}>←</span>
              <span>Volver al dashboard</span>
            </button>
          )}
          <h1 className="intra-news-title">Buzón de sugerencias</h1>
        </div>
      </header>

      {/* ── ENCABEZADO + BOTÓN ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>Gestión de sugerencias</h2>
        <button type="button" className="btn-action" onClick={() => setShowModal(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.625rem 1.25rem', fontSize: '0.875rem', fontWeight: 600 }}>
          <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>+</span>
          <span>Nueva sugerencia</span>
        </button>
      </div>

      {/* ── FILA: DONUT (izq) + LISTA SCROLLABLE (der) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '1.25rem', marginBottom: '1.5rem' }}>

        {/* DONUT CARD */}
        <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem' }}>
          <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#1e293b', margin: 0, alignSelf: 'flex-start' }}>Distribución</h3>

          {(() => {
            const datos = [
              { label: 'Recibidas', valor: stats.recibidas, color: '#3b82f6' },
              { label: 'En revisión', valor: stats.enRevision, color: '#f59e0b' },
              { label: 'Atendidas', valor: stats.atendidas, color: '#10b981' },
            ];
            const total = stats.total;
            const cx = 80, cy = 80, r = 58, grosor = 18;
            const circunferencia = 2 * Math.PI * r;
            // Acumulamos el offset de cada segmento
            let offset = 0;
            const segs = datos.map(d => {
              const pct = total === 0 ? 0 : d.valor / total;
              const dash = pct * circunferencia;
              const gap = circunferencia - dash;
              const rot = (offset / circunferencia) * 360 - 90; // -90 para empezar arriba
              offset += dash;
              return { ...d, dash, gap, rot };
            });
            return (
              <svg viewBox="0 0 160 160" width="170" height="170">
                {/* Pista de fondo */}
                <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth={grosor} />
                {/* Segmentos */}
                {total === 0 ? null : segs.map((s, i) => s.dash > 0.5 && (
                  <circle
                    key={i}
                    cx={cx} cy={cy} r={r}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={grosor}
                    strokeDasharray={`${s.dash.toFixed(2)} ${s.gap.toFixed(2)}`}
                    strokeDashoffset={0}
                    transform={`rotate(${s.rot.toFixed(2)} ${cx} ${cy})`}
                    strokeLinecap="butt"
                  />
                ))}
                {/* Número total centrado */}
                <text x={cx} y={cy - 6} textAnchor="middle" fontSize="28" fontWeight="800" fill="#1e293b">{total}</text>
                <text x={cx} y={cy + 14} textAnchor="middle" fontSize="11" fill="#94a3b8">sugerencias</text>
              </svg>
            );
          })()}

          {/* Leyenda */}
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {[
              { label: 'Recibidas', valor: stats.recibidas, color: '#3b82f6', bg: '#eff6ff' },
              { label: 'En revisión', valor: stats.enRevision, color: '#f59e0b', bg: '#fffbeb' },
              { label: 'Atendidas', valor: stats.atendidas, color: '#10b981', bg: '#f0fdf4' },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.4rem 0.625rem', backgroundColor: item.bg, borderRadius: '7px', border: `1px solid ${item.color}25` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.9375rem', color: '#475569', fontWeight: 500 }}>{item.label}</span>
                </div>
                <span style={{ fontSize: '1.25rem', fontWeight: 700, color: item.color }}>{item.valor}</span>
              </div>
            ))}
            <div style={{ marginTop: '0.375rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Progreso</span>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#10b981' }}>
                  {stats.total === 0 ? 0 : Math.round((stats.atendidas * 100) / stats.total)}%
                </span>
              </div>
              <div style={{ height: '5px', backgroundColor: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${stats.total === 0 ? 0 : Math.round((stats.atendidas * 100) / stats.total)}%`, backgroundColor: '#10b981', transition: 'width 0.4s ease', borderRadius: '3px' }} />
              </div>
            </div>
          </div>
        </div>

        {/* LISTA SCROLLABLE */}
        <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
          {/* Título */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexShrink: 0 }}>
            <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>
              Todas las sugerencias
              <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: '#94a3b8', fontWeight: 400 }}>({sugerenciasFiltradasAdmin.length})</span>
            </h3>
          </div>
          {/* Filtros — siempre en una sola fila */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', flexShrink: 0, flexWrap: 'nowrap' }}>
            <input name="texto" type="text" className="intra-input" value={filtrosAdmin.texto} onChange={handleFiltrosAdminChange}
              placeholder="Buscar…" style={{ fontSize: '0.85rem', padding: '0.4rem 0.65rem', flex: '1 1 0', minWidth: 0 }} />
            <select name="categoria" className="intra-select" value={filtrosAdmin.categoria} onChange={handleFiltrosAdminChange}
              style={{ fontSize: '0.85rem', padding: '0.4rem 0.5rem', flex: '1 1 0', minWidth: 0 }}>
              <option value="Todas">Todas las categorías</option>
              {dbCategorias.map((c, idx) => (
                <option key={c.idCategoria || idx} value={c.nombre}>
                  {c.nombre}
                </option>
              ))}
            </select>
            <select name="estado" className="intra-select" value={filtrosAdmin.estado} onChange={handleFiltrosAdminChange}
              style={{ fontSize: '0.85rem', padding: '0.4rem 0.5rem', flex: '1 1 0', minWidth: 0 }}>
              {ESTADOS.map((e, idx) => <option key={e.value || idx} value={e.value}>{e.label}</option>)}
            </select>
          </div>

          {sugerenciasFiltradasAdmin.length === 0 ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>
              No hay sugerencias que coincidan con los filtros.
            </div>
          ) : (
            <div style={{ overflowY: 'auto', maxHeight: '340px', display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingRight: '0.25rem' }}>
              {sugerenciasFiltradasAdmin.map(s => {
                const etiqueta = getEstadoLabel(s.estado);
                const colorMap = { 'Recibida': '#3b82f6', 'En revisión': '#f59e0b', 'Atendida': '#10b981' };
                const bgMap = { 'Recibida': '#eff6ff', 'En revisión': '#fffbeb', 'Atendida': '#f0fdf4' };
                const col = colorMap[etiqueta] || '#64748b';
                const bg = bgMap[etiqueta] || '#f8fafc';
                const isSelected = selectedId === s.id;
                return (
                  <div key={s.id} onClick={() => handleSeleccionarSugerencia(s)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.625rem 0.875rem',
                      borderTop: `1px solid ${isSelected ? col : '#e2e8f0'}`,
                      borderRight: `1px solid ${isSelected ? col : '#e2e8f0'}`,
                      borderBottom: `1px solid ${isSelected ? col : '#e2e8f0'}`,
                      borderLeft: `4px solid ${col}`,
                      borderRadius: '8px', cursor: 'pointer', backgroundColor: isSelected ? bg : 'white', transition: 'background 0.15s'
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'white'; }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {s.asunto || '(Sin asunto)'}
                      </p>
                      <p style={{ margin: '0.2rem 0 0', fontSize: '0.875rem', color: '#94a3b8' }}>
                        {s.fechaEnvio} · {s.categoria} · {s.anonimo ? 'Anónimo' : s.autor}
                      </p>
                    </div>
                    <span style={{ flexShrink: 0, padding: '0.3rem 0.7rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600, color: col, backgroundColor: bg, border: `1px solid ${col}35` }}>
                      {etiqueta}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* MODAL NUEVA SUGERENCIA */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowModal(false)}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}
            onClick={e => e.stopPropagation()}>
            <div className="intra-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem 2rem', borderBottom: '1px solid #e2e8f0' }}>
              <h2 className="intra-panel-title" style={{ margin: 0, fontSize: '1.25rem' }}>Nueva sugerencia</h2>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button type="button" onClick={handleEnviarSugerencia} style={{ background: 'none', border: 'none', fontSize: '1.5rem', lineHeight: 1, color: '#16a34a', cursor: 'pointer' }} title="Guardar">✓</button>
                <button type="button" onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '2rem', lineHeight: 1, color: '#64748b', cursor: 'pointer' }}>×</button>
              </div>
            </div>
            <div style={{ padding: '2rem', overflowY: 'auto' }}>
              <form className="intra-avisos-form" onSubmit={handleEnviarSugerencia}>
                <div className="intra-form-group">
                  <label className="intra-label">Asunto</label>
                  <input name="asunto" type="text" className={`intra-input ${erroresNueva.asunto ? 'intra-input-error' : ''}`}
                    value={nuevaSug.asunto} onChange={handleNuevaSugChange} placeholder="Ej. Propuesta para mejorar el proceso de inducción" />
                  {erroresNueva.asunto && <p className="intra-error-text">{erroresNueva.asunto}</p>}
                </div>
                <div className="intra-form-group">
                  <label className="intra-label">Descripción</label>
                  <textarea name="descripcion" className={`intra-textarea ${erroresNueva.descripcion ? 'intra-input-error' : ''}`}
                    rows={6} value={nuevaSug.descripcion} onChange={handleNuevaSugChange}
                    placeholder="Describe tu sugerencia, el problema que resuelve y el beneficio que genera." />
                  {erroresNueva.descripcion && <p className="intra-error-text">{erroresNueva.descripcion}</p>}
                </div>
                <div className="intra-form-row">
                  <div className="intra-form-group">
                    <label className="intra-label">Categoría</label>
                    <select name="idCategoria" className="intra-select" value={nuevaSug.idCategoria} onChange={handleNuevaSugChange}>
                      {dbCategorias.map((c, idx) => (
                        <option key={c.idCategoria || idx} value={c.idCategoria}>{c.nombre}</option>
                      ))}
                    </select>
                  </div>
                  <div className="intra-form-group">
                    <label className="intra-label">Identificación</label>
                    <label className="intra-checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <input type="checkbox" name="anonimo" checked={nuevaSug.anonimo} onChange={handleNuevaSugChange} />
                      <span>Enviar como anónimo</span>
                    </label>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DETALLE SUGERENCIA */}
      {sugerenciaSeleccionada && (() => {
        const myId = getSessionUserId();
        const currentEstado = sugerenciaSeleccionada.estado;
        const hasResponsable = !!(sugerenciaSeleccionada.idResponsable);
        // puedeActualizar: soy el responsable asignado (viene del SP)
        const puedeActualizar = sugerenciaSeleccionada.puedeActualizar
          // Fallback local por si el SP no lo devuelve aún (ej. antes de correr el nuevo SP en BD)
          || (sugerenciaSeleccionada.idResponsable == myId);

        // ── Lógica de visibilidad ──────────────────────────────────────────
        // Admin siempre ve el select para asignar / reasignar responsable
        const showResponsableSelect = amIAdmin;

        // Flujo de estados: disponible si soy el responsable (con o sin ser admin)
        const showBotonEnRevision = puedeActualizar && currentEstado === 'recibida' && hasResponsable;
        const showBotonCompletada = puedeActualizar && currentEstado === 'en_revision';
        const showComments = puedeActualizar && (currentEstado === 'en_revision' || currentEstado === 'atendida');

        const canUpdate = showResponsableSelect || showBotonEnRevision || showBotonCompletada || showComments;

        // Handler: asignar responsable y pasar a "en_revision" automáticamente
        const handleAsignarResponsable = async () => {
          const idResp = sugerenciaSeleccionada.idResponsable;
          if (!idResp) return;
          const userObj = dbUsuarios.find(u => (u.idUsuario || u.IdUsuario) == idResp);
          const nombreResp = userObj
            ? (userObj.nombrePersona || userObj.NombrePersona || userObj.nombreUsuario || userObj.NombreUsuario || 'Responsable')
            : sugerenciaSeleccionada.responsable || 'Responsable';

          // Guardar en backend primero
          const success = await updateSugerenciaBackend(sugerenciaSeleccionada.id, {
            estado: 'en_revision',
            idResponsable: idResp,
            responsable: nombreResp,
            comentariosInternos: sugerenciaSeleccionada.comentariosInternos,
          });

          if (success) {
            setSelectedId(null);
          }
        };

        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
            onClick={() => setSelectedId(null)}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}
              onClick={e => e.stopPropagation()}>
              <div className="intra-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem 2rem', borderBottom: '1px solid #e2e8f0' }}>
                <h2 className="intra-panel-title" style={{ margin: 0, fontSize: '1.25rem' }}>Detalle de sugerencia</h2>
                <button type="button" onClick={() => setSelectedId(null)} style={{ background: 'none', border: 'none', fontSize: '2rem', lineHeight: 1, color: '#64748b', cursor: 'pointer' }}>×</button>
              </div>
              <div style={{ padding: '2rem', overflowY: 'auto' }}>

                {/* Metadatos */}
                <p style={{ marginTop: 0, fontSize: '1rem', lineHeight: 1.6 }}>
                  Enviada por <strong>{sugerenciaSeleccionada.anonimo ? "Anónimo" : sugerenciaSeleccionada.autor}</strong> · {sugerenciaSeleccionada.fechaEnvio}
                  {' · '}
                  <span style={{ fontWeight: 600, color: sugerenciaSeleccionada.estado === 'atendida' ? '#10b981' : sugerenciaSeleccionada.estado === 'en_revision' ? '#f59e0b' : '#3b82f6' }}>
                    {getEstadoLabel(sugerenciaSeleccionada.estado)}
                  </span>
                </p>
                <h3 style={{ fontSize: '1.25rem', color: '#1e293b', marginBottom: '1rem' }}>{sugerenciaSeleccionada.asunto}</h3>
                <p style={{ whiteSpace: 'pre-wrap', color: '#475569', lineHeight: 1.6, marginBottom: '2rem' }}>{sugerenciaSeleccionada.descripcion}</p>

                <hr style={{ border: 0, borderTop: '1px solid #e2e8f0', marginBottom: '1.5rem' }} />

                {/* ── SELECT DE RESPONSABLE (solo Admin) ── */}
                {showResponsableSelect && (
                  <div className="intra-form-group" style={{ marginBottom: '1.5rem' }}>
                    <label className="intra-label">
                      {hasResponsable ? 'Responsable asignado (puedes reasignar)' : 'Asignar responsable'}
                    </label>
                    {!hasResponsable && (
                      <p style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.4rem', marginTop: 0 }}>
                        Al asignar, la sugerencia pasará automáticamente a <strong>En revisión</strong>.
                      </p>
                    )}
                    <select className="intra-select"
                      value={sugerenciaSeleccionada.idResponsable || ""}
                      onChange={e => handleCambiarResponsable(sugerenciaSeleccionada.id, e.target.value)}>
                      <option value="">Seleccionar responsable...</option>
                      {dbUsuarios.map((u, idx) => {
                        const uid = u.idUsuario || u.IdUsuario;
                        const nombre = u.nombrePersona || u.NombrePersona || u.nombreUsuario || u.NombreUsuario || `Usuario ${uid}`;
                        return <option key={uid || idx} value={uid}>{nombre}</option>;
                      })}
                    </select>
                  </div>
                )}

                {/* ── COMENTARIOS (responsable en revisión o atendida) ── */}
                {showComments && (
                  <div className="intra-form-group">
                    <label className="intra-label" htmlFor="comentariosInternosSugModal">Comentarios internos / seguimiento</label>
                    <textarea id="comentariosInternosSugModal" className="intra-textarea" rows={4}
                      value={sugerenciaSeleccionada.comentariosInternos || ""}
                      onChange={e => handleCambiarComentariosInternos(sugerenciaSeleccionada.id, e.target.value)}
                      onBlur={e => handleBlurComentarios(sugerenciaSeleccionada.id, e.target.value)}
                      placeholder="Registra acuerdos, fechas de implementación, responsables, etc." />
                  </div>
                )}

                {/* ── MENSAJE informativo (no admin, sin permisos de actualizar) ── */}
                {!canUpdate && (
                  <div style={{ padding: '1rem', backgroundColor: '#f1f5f9', borderRadius: '8px', textAlign: 'center', color: '#64748b', marginTop: '1rem' }}>
                    {hasResponsable
                      ? `Asignada a: ${sugerenciaSeleccionada.responsable}. Solo el responsable puede actualizar.`
                      : "Sugerencia recibida. Esperando asignación de responsable por un Administrador."}
                  </div>
                )}

                {/* ── BOTONES DE ACCIÓN ── */}
                {(showResponsableSelect || showBotonEnRevision || showBotonCompletada) && (
                  <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>

                    {/* Admin: Asignar / Reasignar responsable → pasa a en_revision */}
                    {showResponsableSelect && sugerenciaSeleccionada.idResponsable && (
                      <button type="button" className="intra-button-primary"
                        style={{ fontSize: '0.875rem', padding: '0.6rem 1.25rem', cursor: 'pointer', backgroundColor: '#6366f1' }}
                        onClick={handleAsignarResponsable}>
                        {hasResponsable ? '↺ Reasignar y marcar en revisión' : '✔ Asignar y marcar en revisión'}
                      </button>
                    )}

                    {/* Responsable: Marcar en revisión (si fue asignado pero aún en "recibida") */}
                    {showBotonEnRevision && !amIAdmin && (
                      <button type="button" className="intra-button-primary"
                        style={{ fontSize: '0.875rem', padding: '0.6rem 1.25rem', cursor: 'pointer' }}
                        onClick={async () => {
                          const success = await updateSugerenciaBackend(sugerenciaSeleccionada.id, {
                            estado: 'en_revision',
                            idResponsable: sugerenciaSeleccionada.idResponsable,
                            responsable: sugerenciaSeleccionada.responsable,
                            comentariosInternos: sugerenciaSeleccionada.comentariosInternos
                          });
                          if (success) setSelectedId(null);
                        }}>✔ Marcar como en revisión
                      </button>
                    )}

                    {/* Responsable: Marcar como completada */}
                    {showBotonCompletada && (
                      <button type="button" className="intra-button-primary"
                        style={{ fontSize: '0.875rem', padding: '0.6rem 1.25rem', cursor: 'pointer', backgroundColor: '#10b981' }}
                        onClick={async () => {
                          const success = await updateSugerenciaBackend(sugerenciaSeleccionada.id, {
                            estado: 'atendida',
                            idResponsable: sugerenciaSeleccionada.idResponsable,
                            responsable: sugerenciaSeleccionada.responsable,
                            comentariosInternos: sugerenciaSeleccionada.comentariosInternos
                          });
                          if (success) setSelectedId(null);
                        }}>✔ Marcar como completada
                      </button>
                    )}

                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
};

export default BuzonSugerencias;
